import cx from 'classnames'
import React from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { navigateToPDP, getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'

import { TrackConfig } from '../../hocs/withTracking'

import styles from './infiniteScrollProductList.module.scss'

interface InfiniteScrollProductListProps {
  productCards: IProductCard[]
  swiperClassName?: string
  gridColCount?: number
  tracker?: string
  productCardDesktopClassName?: string
  productCardExpClassName?: string
  productCardMobileClassName?: string
  wishlistIconOverrideClass?: string
  maxWidth: number
  hideReviews?: boolean
  hideOptions?: boolean
  hideWishlistIcon?: boolean
  productClickTrackConfig?: TrackConfig
  optionLimit: number
  hideProductName?: boolean
  productCardLinkClass?: string
  hideTags?: boolean
  skipTagRefresh?: boolean
  productViewTracker?: string
}

export default function InfiniteScrollProductListLegacy({
  productCards,
  swiperClassName,
  gridColCount,
  tracker,
  productCardDesktopClassName,
  productCardExpClassName,
  productCardMobileClassName,
  wishlistIconOverrideClass,
  maxWidth,
  hideReviews,
  hideOptions,
  hideWishlistIcon,
  productClickTrackConfig,
  optionLimit,
  hideProductName,
  productCardLinkClass,
  hideTags,
  skipTagRefresh,
  productViewTracker,
}: InfiniteScrollProductListProps) {
  const router = useRouter()
  return (
    <div
      className={cx(styles.infiniteScrollWrapper, swiperClassName)}
      style={{ gridTemplateColumns: `repeat(${gridColCount}, 1fr)` }}
    >
      {productCards.map((productCard, idx) => {
        const initialSelectedVariant = getInitialSelectedVariant({
          cardVariants: productCard.product.cardVariants,
        })
        const _tracker = tracker ? `${tracker}__0__${idx}` : undefined
        return (
          <ProductCard
            key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
            className={cx(
              styles.productCard,
              productCardDesktopClassName,
              productCardExpClassName,
              productCardMobileClassName,
            )}
            wishlistIconClassName={cx(styles.productCard__wishlistIcon, wishlistIconOverrideClass)}
            productCard={productCard}
            maxWidth={maxWidth}
            hideReviews={hideReviews}
            hideOptions={hideOptions}
            hideProductName={hideProductName}
            tracker={_tracker}
            skipTagRefresh={skipTagRefresh}
            productClickTrackConfig={
              productClickTrackConfig
                ? {
                    ...productClickTrackConfig,
                    context: {
                      ...productClickTrackConfig.context,
                      row: 0,
                      column: idx,
                      productPosition: idx,
                      contentContainerType:
                        productClickTrackConfig.context?.contentContainerType ||
                        CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
                      contentContainerName:
                        productClickTrackConfig.context?.contentContainerName ||
                        CUSTOM_SECTION_CONTAINER_NAMES.INFINITE_LIST,
                      contentContainerDisplayName:
                        // eslint-disable-next-line i18next/no-literal-string -- telemetry fallback value, not user-facing copy
                        productClickTrackConfig.context?.contentContainerDisplayName || 'Infinite Scroll Product List',
                    },
                  }
                : undefined
            }
            shouldShowMore
            optionLimit={optionLimit}
            onShowMoreClick={({ selectedVariant }) => {
              navigateToPDP({
                slug: productCard.product.slug,
                tracker: _tracker,
                selectedOption: selectedVariant?.displayConfig.value,
                router,
              })
            }}
            hideWishlistIcon={hideWishlistIcon}
            productCardLinkClass={productCardLinkClass}
            hideTags={hideTags}
            productViewTracker={productViewTracker}
          />
        )
      })}
    </div>
  )
}
